import { useState } from 'react';
import { Camera, AlertCircle, Loader2, RotateCcw, CheckCircle2, ShieldCheck } from 'lucide-react';
import { verifyIdentity } from '../../../../services/studentQuizService';
import WebcamCapture from './WebcamCapture';
import { toast } from 'sonner';

const FaceVerifyStep = ({ quizId, onSuccess, onCancel }) => {
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [capturedPreview, setCapturedPreview] = useState(null);
  const [isComparing, setIsComparing] = useState(false);
  const [compareResult, setCompareResult] = useState(null); // { verified: boolean, similarity: number, message: string }
  const [compareError, setCompareError] = useState(null);

  const handleCapture = ({ file, blob }) => {
    setCapturedBlob(blob);
    setCapturedPreview(URL.createObjectURL(blob));
  };

  const handleVerifySubmit = async () => {
    if (!capturedBlob) {
      setCompareError('Please capture a face photo first.');
      return;
    }

    setIsComparing(true);
    setCompareError(null);
    setCompareResult(null);

    const file = new File([capturedBlob], 'webcam_capture.jpg', { type: 'image/jpeg' });
    
    try {
      const res = await verifyIdentity(quizId, file);
      setCompareResult(res);
      
      if (res.verified) {
        toast.success('Identity verified successfully!');
      } else {
        toast.error('Identity verification failed.');
      }
    } catch (err) {
      console.error('Verification error response:', err);
      const data = err?.response?.data;
      if (data && data.verified === false) {
        setCompareResult(data);
      } else {
        const errorMsg = data?.message || data?.error || 'Verification request failed. Please check your network and lighting and try again.';
        setCompareError(errorMsg);
        toast.error('Verification failed.');
      }
    } finally {
      setIsComparing(false);
    }
  };

  const handleTryAgain = () => {
    setCompareResult(null);
    setCompareError(null);
    setCapturedBlob(null);
    setCapturedPreview(null);
  };

  if (compareResult?.verified === true) {
    return (
      <div className="flex flex-col items-center justify-center text-center p-8 bg-base-100 rounded-2xl border border-success/20 shadow-xl space-y-6 animate-fade-in my-4">
        <div className="bg-success/10 text-success p-5 rounded-full animate-success-pop shadow-inner">
          <CheckCircle2 className="w-16 h-16" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-base-content">Verification Successful</h3>
          <p className="text-sm text-gray-500">
            Identity verified with a match similarity of <span className="font-semibold text-success">{compareResult.similarity?.toFixed(2)}%</span>.
          </p>
        </div>
        <p className="text-sm text-gray-600 max-w-md">
          Your identity has been verified successfully. You are now authorized to start the quiz. Good luck!
        </p>
        <div className="pt-2 w-full max-w-xs">
          <button 
            type="button" 
            onClick={onSuccess} 
            className="btn btn-primary w-full gap-2 py-3.5 h-auto min-h-0 text-base font-bold rounded-xl shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-200"
          >
            <ShieldCheck className="w-5 h-5" />
            Start Quiz
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Camera className="w-5 h-5 text-primary" />
          Face Verification Scan
        </h2>
        <p className="text-sm text-gray-500">
          Position your face directly in the camera view frame. We'll run a quick AI match check against the face on your student ID.
        </p>
      </div>

      {compareError && (
        <div className="alert alert-error flex items-start gap-2.5 rounded-xl text-sm border-l-4 border-error animate-shake">
          <AlertCircle className="w-5 h-5 text-error mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-bold">System Error</h4>
            <p>{compareError}</p>
          </div>
        </div>
      )}

      {/* Webcam scanner preview */}
      <div className="relative rounded-2xl overflow-hidden bg-black aspect-video border border-base-300 shadow-xl flex items-center justify-center">
        {!capturedPreview ? (
          <WebcamCapture 
            overlayType="circle"
            guideText="Align your face inside the circle frame"
            onCapture={handleCapture}
            onCancel={onCancel}
          />
        ) : (
          <div className="relative w-full h-full flex items-center justify-center bg-base-200">
            <img 
              src={capturedPreview} 
              alt="Captured Face" 
              className="w-full h-full object-cover scale-x-[-1]"
            />

            {/* Scanning line animation */}
            {isComparing && (
              <div className="absolute inset-0 bg-primary/5 flex items-center justify-center">
                <div className="w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent absolute top-0 left-0 right-0 animate-scan shadow-[0_0_12px_2px_rgba(31,98,255,0.5)]" />
                <div className="bg-black/75 rounded-2xl p-4 flex flex-col items-center gap-3 text-white border border-primary/20 shadow-2xl">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <div className="text-center">
                    <p className="font-semibold text-sm">Comparing faces with ID...</p>
                  </div>
                </div>
              </div>
            )}



            {/* Verification Failure frame overlay */}
            {compareResult?.verified === false && (
              <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-white p-6 animate-fade-in text-center">
                <div className="bg-error text-error-content p-3.5 rounded-full mb-3 animate-bounce">
                  <AlertCircle className="w-9 h-9" />
                </div>
                <h3 className="text-lg font-bold text-error">Face Verification Failed</h3>
                <p className="text-xs text-gray-400 mt-0.5">Similarity: {compareResult.similarity?.toFixed(2)}%</p>
                
                <p className="text-sm text-gray-200 mt-3 max-w-sm">
                  {compareResult.message || 'Face did not match the photo on your student ID. Please ensure your face is well-lit and not covered by glasses or a hat.'}
                </p>

                <div className="flex gap-3 mt-6">
                  <button 
                    type="button"
                    onClick={handleTryAgain}
                    className="btn btn-error btn-sm gap-2 text-white"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Try Again
                  </button>
                  {onCancel && (
                    <button 
                      type="button"
                      onClick={onCancel}
                      className="btn btn-ghost btn-sm text-white"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Manual Verification Actions */}
      {capturedPreview && !isComparing && !compareResult && (
        <div className="flex justify-end gap-3 pt-2">
          <button 
            type="button"
            onClick={handleTryAgain}
            className="btn btn-outline gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Retake Photo
          </button>
          <button 
            type="button"
            onClick={handleVerifySubmit}
            className="btn btn-primary gap-2 px-6"
          >
            <ShieldCheck className="w-4 h-4" />
            Submit Face Verify
          </button>
        </div>
      )}

      <style>{`
        @keyframes scan {
          0% { top: 0%; }
          50% { top: 100%; }
          100% { top: 0%; }
        }
        .animate-scan {
          animation: scan 2.5s ease-in-out infinite;
        }
        .animate-fade-in {
          animation: fadeIn 0.4s ease-out forwards;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-success-pop {
          animation: successPop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
        @keyframes successPop {
          from { transform: scale(0.6); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-6px); }
          40%, 80% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
};

export default FaceVerifyStep;
